/*#
# Tag colors

One registry decides how every tag is colored, everywhere it appears: chips
on cards and in the card window, and the dots beside items in the filter
menu. Resolution order: an exact rule for the tag (`priority:critical`), a
rule for its key (`kind:`), then a color derived from the key so tags of one
family look alike. The defaults make the tags that must stand out loud:
priorities, bugs, and blockers.

```js
import { tagColor, setTagColor } from 'tosijs-virta'
import { elements } from 'tosijs'
const { div, span } = elements

setTagColor('area:', { bg: '#0b5cad', ink: '#ffffff' })      // a whole key
setTagColor('kind:question', { bg: '#7f5f01', ink: '#ffffff' }) // one tag
const chip = (tag) => { const c = tagColor(tag); return span({ style: { background: c.bg, color: c.ink, padding: '2px 8px', borderRadius: '4px', marginRight: '6px', fontWeight: c.loud ? 700 : 500 } }, tag) }
preview.append(div(...['priority:critical', 'priority:low', 'kind:bug', 'blocked', 'area:store', 'kind:question', 'kind:docs', 'upstream'].map(chip)))
setTagColor('area:', null)
setTagColor('kind:question', null)
```
```test
test('eight chips, the loud ones bold', () => {
  const chips = Array.from(preview.querySelectorAll('span'))
  expect(chips.length).toBe(8)
  expect(chips.filter((c) => c.style.fontWeight === '700').length).toBe(4)
})
```
*/

/*{ "parent": "Reference", "order": 16 }*/

import { colorFor } from './palette'

export interface TagColor {
  /** css background */
  bg: string
  /** css text color that reads on `bg` */
  ink: string
  /** drawn first and bolder: the tags that must be seen */
  loud?: boolean
}

/** The shipped rules. Keys ending in `:` apply to every value of that key. */
export const DEFAULT_TAG_COLORS: Record<string, TagColor> = {
  'size:': { bg: '#e9f2ff', ink: '#0c66e4' },
  // big tasks call themselves out: most teams break these down
  'size:8': { bg: '#e56910', ink: '#ffffff', loud: true },
  'size:13': { bg: '#c9372c', ink: '#ffffff', loud: true },
  'size:21': { bg: '#c9372c', ink: '#ffffff', loud: true },
  'priority:critical': { bg: '#c9372c', ink: '#ffffff', loud: true },
  'priority:high': { bg: '#e56910', ink: '#ffffff', loud: true },
  'priority:medium': { bg: '#e2b203', ink: '#1c1200', loud: true },
  'priority:low': { bg: '#8590a2', ink: '#ffffff', loud: true },
  'kind:bug': { bg: '#ae2a19', ink: '#ffffff', loud: true },
  blocked: { bg: '#5e4db2', ink: '#ffffff', loud: true },
  'blocked-by:': { bg: '#5e4db2', ink: '#ffffff', loud: true },
  // this task holds up another project: it should stand out on its own board
  'blocks:': { bg: '#5e4db2', ink: '#ffffff', loud: true },
  'delays:': { bg: '#b87316', ink: '#ffffff', loud: true },
}

const rules = new Map<string, TagColor>(Object.entries(DEFAULT_TAG_COLORS))

/**
 * Add or remove a rule. `tag` is an exact tag (`kind:bug`), a bare tag
 * (`blocked`), or a key with a trailing colon (`area:`) for every value.
 * `null` removes the rule (a shipped default included).
 */
export function setTagColor(tag: string, color: TagColor | null): void {
  if (color) rules.set(tag, { ...color })
  else rules.delete(tag)
}

/** Every rule currently in force (defaults plus yours). */
export function tagColorRules(): Record<string, TagColor> {
  return Object.fromEntries(rules)
}

/** The color for a tag: exact rule, key rule, else the key's family tint. */
export function tagColor(tag: string): TagColor {
  const exact = rules.get(tag)
  if (exact) return exact
  const i = tag.indexOf(':')
  const key = i < 0 ? tag : tag.slice(0, i)
  const byKey = rules.get(`${key}:`)
  if (byKey) return byKey
  if (i < 0) return NEUTRAL
  const c = colorFor(key)
  // a faint tint of the family color, inked with that color pulled toward
  // the board's text — dark on a light board, light on a dark one
  return {
    bg: `${c.bg}22`,
    ink: `color-mix(in srgb, ${c.deep} 55%, var(--virta-text, #172b4d))`,
  }
}

/** Untagged-family chips: the board's own chip colors, light defaults off a board. */
const NEUTRAL: TagColor = {
  bg: 'var(--virta-chip, #e4e6ea)',
  ink: 'var(--virta-subtle, #44546f)',
}
